/**
 * The growth machine — three engines, one launch window.
 *
 * This file is the strategy source of truth rendered by /strategy. Update it as
 * decisions land (statuses move idea → mocked → approved → building → live);
 * treat it like the work log, not a one-off deck.
 */

export type NodeStatus = 'idea' | 'mocked' | 'approved' | 'building' | 'live';

export type StrategyNode = {
  id: string;
  title: string;
  status: NodeStatus;
  /** One-line card summary. */
  summary: string;
  /** Expanded detail — the why and the how. */
  detail: string[];
  dependsOn?: string[];
  link?: { to: string; label: string };
};

export type StrategyEngine = {
  id: string;
  name: string;
  tagline: string;
  nodes: StrategyNode[];
};

/** Fall syllabus week — the single best acquisition window of the year for 18–25 college users. */
export const LAUNCH_TARGET = { label: 'Syllabus week', dateISO: '2026-08-24' };

export const LAUNCH_GATE = {
  title: 'Launch gate: App Store availability',
  body:
    'The listing is live (id6762528411) but not for sale pending the Apple account migration. Every engine below ends at a download — so the machine gets built now and fired at launch. Timing target: syllabus week.',
};

/** The flywheel, in order. Rendered as the loop strip at the bottom of /strategy. */
export const MACHINE_LOOP = [
  'Venue portal',
  'QR in the venue',
  'Student ratings',
  'Rankings',
  'GEO pages + rivalry content',
  'Downloads',
  'Stronger outreach numbers',
];

export const STRATEGY_ENGINES: StrategyEngine[] = [
  {
    id: 'venue-outreach',
    name: 'Venue Outreach',
    tagline: 'Personalized data beats pitch decks — “you’re #3 of 41 in your city” is the open.',
    nodes: [
      {
        id: 'venue-pipeline',
        title: 'Venue relationship pipeline',
        status: 'live',
        summary: 'CRM board tracking every venue conversation from first touch to partnered.',
        detail: [
          'Already running as the Venue Relations board — statuses from not_contacted through partnered.',
          'Every outreach touch gets logged here so the pipeline survives context switches between sessions and people.',
        ],
        link: { to: '/venue-relations', label: 'Open Venue Relations' },
      },
      {
        id: 'target-lists',
        title: 'Data-driven target lists',
        status: 'idea',
        summary: 'Outreach order comes from our own data: venues students already rate, per launch city.',
        detail: [
          'Rank venues by unique raters and score inside each launch city — those venues have proof waiting in the pitch.',
          'The venue table already holds everything needed (raters, score, city); this is a filtered view, not new data.',
        ],
      },
      {
        id: 'one-pager',
        title: 'Per-venue one-pager',
        status: 'mocked',
        summary: 'A personalized “your Shado stats” page per venue — the asset that opens the conversation.',
        detail: [
          'Merge fields filled from live data: rank in city, unique raters, score.',
          'A hosted link works in any channel (IG DM, email, in person); an HTML email only works in email.',
        ],
        link: { to: '/mockups', label: 'Review the mockup' },
      },
      {
        id: 'qr-assets',
        title: 'QR table tents & stickers',
        status: 'mocked',
        summary: 'Free print asset for the venue — their customers rate them, they market Shado for us.',
        detail: [
          'The ask that converts is not “partner with us” — it’s “you’re ranked, here’s a free table tent so your customers rate you.”',
          'Generated per venue in the Content Studio; QR points at the App Store listing.',
        ],
        dependsOn: ['one-pager'],
        link: { to: '/studio', label: 'Generate in Studio' },
      },
      {
        id: 'email-module',
        title: 'Outreach email module',
        status: 'mocked',
        summary: 'Merge-field HTML email for the venues that are actually reachable by email.',
        detail: [
          'Real sending at scale is a deliverability project: separate sending domain, warmup, SPF/DKIM/DMARC, an ESP — never from the main domain.',
          'Email is the secondary channel; venues live in Instagram DMs.',
        ],
      },
      {
        id: 'ig-dm-motion',
        title: 'IG DM delivery motion',
        status: 'idea',
        summary: 'Dashboard generates the personalized asset; a human pastes the link into the DM.',
        detail: [
          'Automating IG DMs violates ToS and reads as spam — the scalable version is semi-manual delivery of a generated link.',
          'This hybrid genuinely scales to hundreds of venues; “fully automated” scales to a spam folder.',
        ],
        dependsOn: ['one-pager', 'target-lists'],
      },
    ],
  },
  {
    id: 'geo-web',
    name: 'GEO / Public Web',
    tagline: 'Be the source LLMs cite for “best bars near campus” — unique ranked data is GEO gold.',
    nodes: [
      {
        id: 'city-pages',
        title: 'Programmatic city pages',
        status: 'mocked',
        summary: 'shado.app/nightlife/{city} for all 267 metros, rendered from live rankings.',
        detail: [
          'Server-rendered pages with schema.org ItemList/AggregateRating markup, refreshed from the DB on a schedule.',
          'Nobody else has comparative Elo rankings from verified students — unique data with a stated methodology is exactly what generative engines cite.',
          'This is a real second product surface (public web app on Vercel reading the same Supabase) — the biggest lift on the map, and the one that compounds forever.',
        ],
        link: { to: '/mockups', label: 'Review the mockup' },
      },
      {
        id: 'venue-pages',
        title: 'Programmatic venue pages',
        status: 'idea',
        summary: 'One crawlable page per ranked venue, linked from its city page.',
        detail: [
          'LocalBusiness + AggregateRating markup per venue; doubles as the hosted one-pager target for outreach.',
        ],
        dependsOn: ['city-pages'],
      },
      {
        id: 'crawlability',
        title: 'Crawlability layer',
        status: 'idea',
        summary: 'llms.txt, sitemaps, OG cards, fast server-rendered HTML.',
        detail: [
          'Table stakes for both classic SEO and generative retrieval — cheap once the pages exist.',
        ],
        dependsOn: ['city-pages'],
      },
      {
        id: 'data-drops',
        title: 'Ranking data drops (PR)',
        status: 'idea',
        summary: '“Shado College Nightlife Rankings — Fall 2026”, per campus. The rankings are the content.',
        detail: [
          'The Letterboxd / RateMyProfessor playbook: publish the data, let the arguments distribute it.',
          'Rankings are inherently controversial, and controversy is inherently shareable.',
        ],
        dependsOn: ['city-pages'],
      },
      {
        id: 'reddit-presence',
        title: 'Reddit / Quora presence',
        status: 'idea',
        summary: 'Authentic answers in r/{university} “where do people actually go out” threads.',
        detail: [
          'These threads are disproportionately weighted in LLM retrieval.',
          'Authentic participation only — astroturfing gets clocked instantly by this audience.',
        ],
      },
    ],
  },
  {
    id: 'guerrilla-campus',
    name: 'Guerrilla / Campus',
    tagline: 'Density is the product — one saturated campus beats 267 invisible metros.',
    nodes: [
      {
        id: 'launch-campuses',
        title: 'Pick 1–3 launch campuses',
        status: 'approved',
        summary: 'Michigan + Tulane seeded on the Campus Growth board. Saturate before expanding.',
        detail: [
          'The app is only good when your friends and your bars are on it — concentration is the whole strategy.',
          'A campus where 15% of students have it is a phenomenon that spreads to the next campus on its own.',
        ],
        link: { to: '/campus-growth', label: 'Open Campus Growth' },
      },
      {
        id: 'rivalry-content',
        title: 'Rivalry ranking content',
        status: 'mocked',
        summary: '“Every bar at {school}, ranked by actual students” — arguments are distribution.',
        detail: [
          'TikTok/Reels format generated straight from live rankings in the Content Studio.',
          'Bar rankings start arguments; every argument is reach.',
        ],
        link: { to: '/studio', label: 'Generate in Studio' },
      },
      {
        id: 'meme-accounts',
        title: 'Campus IG meme/nightlife accounts',
        status: 'idea',
        summary: '$50–200 per story post — the cheapest CAC in this demographic.',
        detail: [
          'Track handles and deals on the Influencer Outreach board.',
        ],
        link: { to: '/influencer-outreach', label: 'Open Influencer Outreach' },
      },
      {
        id: 'ambassadors',
        title: 'Campus ambassador nodes',
        status: 'idea',
        summary: 'Greek life, promoters, nightlife photographers — people already paid to know where everyone goes.',
        detail: [
          'Ambassadors get the QR assets and the rivalry content; their incentive is being the plug.',
        ],
        dependsOn: ['launch-campuses'],
      },
      {
        id: 'in-venue-qr',
        title: 'In-venue QR placement',
        status: 'idea',
        summary: 'Bathroom stalls and table tents at the ranked bars — sanctioned, via the venue pipeline.',
        detail: [
          'Placed with venue blessing through the outreach engine, so it is not even guerrilla — it is a partnership deliverable.',
        ],
        dependsOn: ['qr-assets', 'venue-pipeline'],
      },
      {
        id: 'syllabus-window',
        title: 'Syllabus-week timing',
        status: 'building',
        summary: 'Everything on this map backs into the fall semester start.',
        detail: [
          'The single best acquisition window of the year for 18–25 college users.',
          'If launch slips past it, the next comparable window is spring semester — plan accordingly.',
        ],
      },
    ],
  },
];
